import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useWeb3V3 } from '../contexts/Web3ContextV3';
import toast from 'react-hot-toast';

/**
 * V3 My Assets Page - Black & White Premium Theme
 * NFT List + Dividend Management + Winning Records
 */
const MyAssetsV3 = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const {
    account,
    contracts,
    connectWallet,
    isConnecting,
    getUserNFTs,
    getUserUnclaimedDraws,
    getClaimableDividends,
    claimDividend,
    claimDividendsBatch,
    getDrawResult,
    claimPrize,
    claimPrizesBatch
  } = useWeb3V3();

  // State management
  const [loading, setLoading] = useState(true);
  const [myNFTs, setMyNFTs] = useState([]);
  const [unclaimedDraws, setUnclaimedDraws] = useState([]);
  const [claimableDividends, setClaimableDividends] = useState('0');
  const [selectedTab, setSelectedTab] = useState('nfts'); // 'nfts' | 'dividends' | 'prizes'
  const [processingClaim, setProcessingClaim] = useState(false);

  // Prize information
  const [prizeInfo, setPrizeInfo] = useState([]);

  // Load user data
  useEffect(() => {
    if (account && contracts.lotteryNFT) {
      loadUserAssets();
    }
  }, [account, contracts]);

  const loadUserAssets = async () => {
    try {
      setLoading(true);

      // Load all data in parallel
      const [nfts, unclaimed] = await Promise.all([
        getUserNFTs(),
        getUserUnclaimedDraws()
      ]);

      setMyNFTs(nfts || []);
      setUnclaimedDraws(unclaimed || []);

      // If there are unclaimed periods, query claimable dividends
      if (unclaimed && unclaimed.length > 0) {
        const dividends = await getClaimableDividends(unclaimed);
        setClaimableDividends(dividends);
      }

      // Check winning status for each NFT
      if (nfts && nfts.length > 0 && contracts.prizePool) {
        await checkPrizeInfo(nfts);
      }

    } catch (error) {
      console.error('Failed to load assets:', error);
      toast.error(t('assets.error.loadFailed'));
    } finally {
      setLoading(false);
    }
  };

  // Check NFT winning information
  const checkPrizeInfo = async (nfts) => {
    const prizes = [];

    for (const nft of nfts) {
      try {
        // Get all draw periods that the NFT participated in (from minting period to current period)
        const mintDrawId = parseInt(nft.drawId);
        const currentDrawId = await contracts.prizePool.currentDrawId();
        const current = parseInt(currentDrawId.toString());

        // Check if won in each period
        for (let drawId = mintDrawId; drawId <= current; drawId++) {
          try {
            const winnerInfo = await contracts.prizePool.getWinnerInfo(drawId, nft.tokenId);

            // If there is winning info and unclaimed
            if (winnerInfo && winnerInfo.level > 0 && !winnerInfo.claimed) {
              const drawResult = await getDrawResult(drawId);
              prizes.push({
                tokenId: nft.tokenId,
                number: nft.number,
                drawId: drawId.toString(),
                level: winnerInfo.level,
                amount: winnerInfo.prizeAmount,
                claimed: winnerInfo.claimed,
                winningNumber: drawResult?.winningNumber || 'N/A'
              });
            }
          } catch (err) {
            // This period may not have drawn yet, ignore
            continue;
          }
        }
      } catch (error) {
        console.error(`Failed to check NFT ${nft.tokenId} winning info:`, error);
      }
    }

    setPrizeInfo(prizes);
  };

  // Claim single period dividend
  const handleClaimDividend = async (drawId) => {
    if (!account) {
      toast.error(t('assets.error.connectWallet'));
      return;
    }

    try {
      setProcessingClaim(true);
      await claimDividend(drawId);
      // Refresh data
      await loadUserAssets();
    } catch (error) {
      console.error('Failed to claim dividend:', error);
    } finally {
      setProcessingClaim(false);
    }
  };

  // Batch claim dividends
  const handleClaimAllDividends = async () => {
    if (!account || unclaimedDraws.length === 0) {
      toast.error(t('assets.error.noDividends'));
      return;
    }

    try {
      setProcessingClaim(true);
      await claimDividendsBatch(unclaimedDraws);
      // Refresh data
      await loadUserAssets();
    } catch (error) {
      console.error('Failed to batch claim dividends:', error);
    } finally {
      setProcessingClaim(false);
    }
  };

  // Claim single prize
  const handleClaimPrize = async (drawId, tokenId) => {
    if (!account) {
      toast.error(t('assets.error.connectWallet'));
      return;
    }

    try {
      setProcessingClaim(true);
      await claimPrize(drawId, tokenId);
      // Refresh data
      await loadUserAssets();
    } catch (error) {
      console.error('Failed to claim prize:', error);
    } finally {
      setProcessingClaim(false);
    }
  };

  // Batch claim prizes
  const handleClaimAllPrizes = async () => {
    if (!account || prizeInfo.length === 0) {
      toast.error(t('assets.error.noPrizes'));
      return;
    }

    try {
      setProcessingClaim(true);
      const drawIds = prizeInfo.map(p => p.drawId);
      const tokenIds = prizeInfo.map(p => p.tokenId);
      await claimPrizesBatch(drawIds, tokenIds);
      // Refresh data
      await loadUserAssets();
    } catch (error) {
      console.error('Failed to batch claim prizes:', error);
    } finally {
      setProcessingClaim(false);
    }
  };

  // Get prize level name
  const getPrizeLevelName = (level) => {
    const levels = ['', t('draws.prizes.third'), t('draws.prizes.second'), t('draws.prizes.first'), t('draws.prizes.grand')];
    return levels[level] || '';
  };

  // Get prize level icon
  const getPrizeLevelIcon = (level) => {
    const icons = ['', '', '', '', ''];
    return icons[level] || '';
  };

  // If wallet not connected
  if (!account) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-4xl font-bold mb-6">{t('assets.title')}</h2>
          <p className="text-gray-400 mb-8">{t('common.pleaseConnectWallet')}</p>
          <button
            onClick={connectWallet}
            disabled={isConnecting}
            className="btn-primary px-8 py-3"
          >
            {isConnecting ? t('common.connecting') : t('common.connectWallet')}
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white py-12">
      <div className="container-v3">
        {/* Page title */}
        <div className="mb-8">
          <h1 className="text-5xl font-black mb-2">{t('assets.title')}</h1>
          <p className="text-gray-400">
            {t('assets.subtitle')}
          </p>
        </div>

        {/* Statistics cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          {/* NFT count */}
          <div className="card p-6 text-center">
            <div className="text-gray-500 text-sm mb-2 uppercase tracking-wider">{t('assets.myNFTs')}</div>
            <div className="text-4xl font-bold font-mono">{myNFTs.length}</div>
            <div className="text-gray-600 text-xs mt-2">{t('assets.stats.pieces')}</div>
          </div>

          {/* Unclaimed dividends */}
          <div className="card p-6 text-center">
            <div className="text-gray-500 text-sm mb-2 uppercase tracking-wider">{t('assets.claimableDividends')}</div>
            <div className="text-4xl font-bold font-mono text-white">{parseFloat(claimableDividends).toFixed(2)}</div>
            <div className="text-gray-600 text-xs mt-2">USDT</div>
          </div>

          {/* Unclaimed periods */}
          <div className="card p-6 text-center">
            <div className="text-gray-500 text-sm mb-2 uppercase tracking-wider">{t('assets.unclaimedPeriods')}</div>
            <div className="text-4xl font-bold font-mono">{unclaimedDraws.length}</div>
            <div className="text-gray-600 text-xs mt-2">{t('assets.stats.periods')}</div>
          </div>

          {/* Win count */}
          <div className="card-glow p-6 text-center">
            <div className="text-gray-500 text-sm mb-2 uppercase tracking-wider">{t('assets.winCount')}</div>
            <div className="text-4xl font-bold font-mono text-white">{prizeInfo.length}</div>
            <div className="text-gray-600 text-xs mt-2">{t('assets.stats.times')}</div>
          </div>
        </div>

        {/* Tab navigation */}
        <div className="border-b border-gray-800 mb-8">
          <div className="flex gap-8">
            <button
              onClick={() => setSelectedTab('nfts')}
              className={`pb-4 px-2 font-medium transition-all ${
                selectedTab === 'nfts'
                  ? 'border-b-2 border-white text-white'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {t('assets.tabs.nfts')} ({myNFTs.length})
            </button>
            <button
              onClick={() => setSelectedTab('dividends')}
              className={`pb-4 px-2 font-medium transition-all ${
                selectedTab === 'dividends'
                  ? 'border-b-2 border-white text-white'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {t('assets.tabs.dividends')} ({unclaimedDraws.length})
            </button>
            <button
              onClick={() => setSelectedTab('prizes')}
              className={`pb-4 px-2 font-medium transition-all ${
                selectedTab === 'prizes'
                  ? 'border-b-2 border-white text-white'
                  : 'text-gray-500 hover:text-gray-300'
              }`}
            >
              {t('assets.tabs.prizes')} ({prizeInfo.length})
            </button>
          </div>
        </div>

        {/* Content area */}
        {loading ? (
          <div className="text-center py-20">
            <div className="text-gray-500 text-xl">{t('common.loading')}</div>
          </div>
        ) : (
          <>
            {/* NFT list */}
            {selectedTab === 'nfts' && (
              <div>
                {myNFTs.length === 0 ? (
                  <div className="text-center py-20">
                    <h3 className="text-2xl font-bold mb-4">{t('assets.nft.empty')}</h3>
                    <p className="text-gray-400 mb-8">{t('assets.nft.emptyDesc')}</p>
                    <button
                      onClick={() => navigate('/mint')}
                      className="btn-primary px-8 py-3"
                    >
                      {t('assets.nft.mintNow')}
                    </button>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {myNFTs.map((nft) => (
                      <div key={nft.tokenId} className="card p-6 hover:border-white transition-all">
                        {/* NFT number */}
                        <div className="text-center mb-4">
                          <div className="text-sm text-gray-500 mb-2">{t('assets.nft.number')} #{nft.tokenId}</div>
                          <div className="text-5xl font-black font-mono tracking-wider mb-2">
                            {nft.number}
                          </div>
                        </div>

                        {/* Signature */}
                        {nft.signature && (
                          <div className="mb-4 p-3 bg-black border border-gray-800">
                            <div className="text-xs text-gray-500 mb-1">{t('assets.nft.signature')}</div>
                            <div className="text-sm text-gray-300 italic">{nft.signature}</div>
                          </div>
                        )}

                        <div className="divider"></div>

                        {/* NFT info */}
                        <div className="space-y-2 text-sm">
                          <div className="flex justify-between">
                            <span className="text-gray-500">{t('assets.nft.mintDraw')}</span>
                            <span className="font-mono">#{nft.drawId}</span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">{t('assets.nft.totalDividends')}</span>
                            <span className="font-mono font-bold text-white">
                              {parseFloat(nft.totalDividends).toFixed(2)} USDT
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">{t('assets.nft.totalPrizes')}</span>
                            <span className="font-mono font-bold text-white">
                              {parseFloat(nft.totalPrizes).toFixed(2)} USDT
                            </span>
                          </div>
                          <div className="flex justify-between">
                            <span className="text-gray-500">{t('assets.nft.mintTime')}</span>
                            <span className="text-xs">
                              {new Date(parseInt(nft.mintTimestamp) * 1000).toLocaleDateString()}
                            </span>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Dividend records */}
            {selectedTab === 'dividends' && (
              <div>
                {/* Batch claim button */}
                {unclaimedDraws.length > 0 && (
                  <div className="mb-6 flex items-center justify-between card p-4">
                    <div>
                      <div className="text-sm text-gray-400">{t('assets.dividends.total')}</div>
                      <div className="text-3xl font-bold font-mono">
                        {parseFloat(claimableDividends).toFixed(2)} <span className="text-lg text-gray-500">USDT</span>
                      </div>
                    </div>
                    <button
                      onClick={handleClaimAllDividends}
                      disabled={processingClaim}
                      className="btn-primary px-8 py-3"
                    >
                      {processingClaim ? t('assets.dividends.claiming') : `${t('assets.dividends.claimAll')} (${unclaimedDraws.length}${t('assets.stats.periods')})`}
                    </button>
                  </div>
                )}

                {/* Dividend list */}
                {unclaimedDraws.length === 0 ? (
                  <div className="text-center py-20">
                    <h3 className="text-2xl font-bold mb-4">{t('assets.dividends.empty')}</h3>
                    <p className="text-gray-400">{t('assets.dividends.emptyDesc')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {unclaimedDraws.map((drawId) => (
                      <div key={drawId} className="card p-6 flex items-center justify-between">
                        <div>
                          <div className="text-lg font-bold mb-1">{t('assets.dividends.draw')} {drawId} {t('assets.dividends.period')}</div>
                          <div className="text-sm text-gray-500">{t('assets.dividends.pending')}</div>
                        </div>
                        <button
                          onClick={() => handleClaimDividend(drawId)}
                          disabled={processingClaim}
                          className="btn-outline px-6 py-2"
                        >
                          {t('assets.dividends.claim')}
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Winning records */}
            {selectedTab === 'prizes' && (
              <div>
                {/* Batch claim button */}
                {prizeInfo.length > 0 && (
                  <div className="mb-6 card p-4 flex items-center justify-between">
                    <div>
                      <div className="text-sm text-gray-400">{t('assets.prizes.unclaimed')}</div>
                      <div className="text-3xl font-bold">{prizeInfo.length} <span className="text-lg text-gray-500">{t('assets.prizes.wins')}</span></div>
                    </div>
                    <button
                      onClick={handleClaimAllPrizes}
                      disabled={processingClaim}
                      className="btn-primary px-8 py-3"
                    >
                      {processingClaim ? t('assets.prizes.claiming') : t('assets.prizes.claimAll')}
                    </button>
                  </div>
                )}

                {/* Winning list */}
                {prizeInfo.length === 0 ? (
                  <div className="text-center py-20">
                    <h3 className="text-2xl font-bold mb-4">{t('assets.prizes.empty')}</h3>
                    <p className="text-gray-400">{t('assets.prizes.emptyDesc')}</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {prizeInfo.map((prize, index) => (
                      <div key={index} className="card-glow p-6">
                        <div className="flex items-start justify-between mb-4">
                          <div>
                            <div className="flex items-center gap-3 mb-2">
                              <span className="text-4xl">{getPrizeLevelIcon(prize.level)}</span>
                              <div>
                                <div className="text-2xl font-bold">{getPrizeLevelName(prize.level)}</div>
                                <div className="text-sm text-gray-500">{t('assets.dividends.draw')} {prize.drawId} {t('assets.dividends.period')}</div>
                              </div>
                            </div>
                          </div>
                          <button
                            onClick={() => handleClaimPrize(prize.drawId, prize.tokenId)}
                            disabled={processingClaim}
                            className="btn-primary px-6 py-2"
                          >
                            {t('assets.prizes.claimPrize')}
                          </button>
                        </div>

                        <div className="divider"></div>

                        <div className="grid grid-cols-2 gap-4 text-sm">
                          <div>
                            <div className="text-gray-500 mb-1">{t('assets.prizes.yourNFT')}</div>
                            <div className="text-2xl font-mono font-bold">{prize.number}</div>
                          </div>
                          <div>
                            <div className="text-gray-500 mb-1">{t('assets.prizes.winningNumber')}</div>
                            <div className="text-2xl font-mono font-bold">{prize.winningNumber}</div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default MyAssetsV3;
